import { useEffect, useRef, useState } from "react";
import { useAuth } from "../../context/AuthContext";
import { dictionary } from "../../utils/dictionary";
import Loader from "../../components/common/Loader";
import {
  getMetaIntegration,
  getMetaOAuthUrl,
  getMetaAdAccounts,
  getMetaBusinesses,
  getCampaignMetrics,
  getAdsTable,
  updateMetaAdAccount,
  MetaIntegration,
  MetaAdAccount,
  MetaBusiness,
  CampaignMetricsResponse,
  AdTableRow,
} from "../../services/meta/metaService";
import { BarChart3, ArrowRight, Users, Eye, MousePointerClick, Video, RefreshCw, Megaphone, ChevronDown, ChevronUp } from "lucide-react";
import toast from "react-hot-toast";
import { DateFilter } from "../../components/sales/DateFilter";

const formatDateToYMD = (date: Date): string => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
};

const getDefaultDateRange = (): { start: string; end: string } => {
  const end = new Date();
  const start = new Date();
  start.setDate(start.getDate() - 30);
  return { start: formatDateToYMD(start), end: formatDateToYMD(end) };
};

const CampaignsPage = () => {
  const { language } = useAuth();

  const [loading, setLoading] = useState(true);
  const [integration, setIntegration] = useState<MetaIntegration | null>(null);
  const [businesses, setBusinesses] = useState<MetaBusiness[]>([]);
  const [selectedBusiness, setSelectedBusiness] = useState<string | null>(null);
  const [adAccounts, setAdAccounts] = useState<MetaAdAccount[]>([]);
  const [selectedAdAccount, setSelectedAdAccount] = useState<string | null>(null);
  const [metrics, setMetrics] = useState<CampaignMetricsResponse | null>(null);
  const [adsTable, setAdsTable] = useState<AdTableRow[]>([]);
  const [loadingMetrics, setLoadingMetrics] = useState(false);
  const [loadingAds, setLoadingAds] = useState(false);
  const [dateStart, setDateStart] = useState(getDefaultDateRange().start);
  const [dateEnd, setDateEnd] = useState(getDefaultDateRange().end);
  const [dateLabel, setDateLabel] = useState("");
  const [connecting, setConnecting] = useState(false);
  const [connectError, setConnectError] = useState<string | null>(null);
  const [sortColumn, setSortColumn] = useState<string>("spend");
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("desc");
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    loadIntegration();
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, []);

  const loadIntegration = async () => {
    setLoading(true);
    try {
      const meta = await getMetaIntegration();
      setIntegration(meta);

      if (meta?.is_active) {
        // Load businesses
        try {
          const biz = await getMetaBusinesses();
          setBusinesses(biz.businesses || []);
          if (meta.business_id) {
            setSelectedBusiness(meta.business_id);
          } else if (biz.businesses?.length > 0) {
            setSelectedBusiness(biz.businesses[0].id);
          }
        } catch {
          // No businesses available
        }

        // Load ad accounts
        const accounts = await getMetaAdAccounts(meta.business_id || undefined);
        const accountList = accounts.adaccounts || [];
        setAdAccounts(accountList);

        const savedAccount = meta.ad_account_id;
        if (savedAccount && accountList.some((a) => a.id === savedAccount)) {
          setSelectedAdAccount(savedAccount);
          await loadData(savedAccount, dateStart, dateEnd);
        } else if (accountList.length > 0) {
          const firstId = accountList[0].id;
          setSelectedAdAccount(firstId);
          await updateMetaAdAccount(firstId);
          await loadData(firstId, dateStart, dateEnd);
        }
      }
    } catch {
      // Expected for users without Meta connected
    } finally {
      setLoading(false);
    }
  };

  const loadData = async (adAccountId: string, start: string, end: string) => {
    await Promise.all([
      loadMetrics(adAccountId, start, end),
      loadAdsTable(adAccountId, start, end),
    ]);
  };

  const loadMetrics = async (adAccountId: string, start: string, end: string) => {
    setLoadingMetrics(true);
    setMetrics(null);
    try {
      const data = await getCampaignMetrics(adAccountId, start, end);
      setMetrics(data);
    } catch {
      toast.error(dictionary["campaigns-load-error"][language]);
    } finally {
      setLoadingMetrics(false);
    }
  };

  const loadAdsTable = async (adAccountId: string, start: string, end: string) => {
    setLoadingAds(true);
    setAdsTable([]);
    try {
      const data = await getAdsTable(adAccountId, start, end);
      setAdsTable(data.ads || []);
    } catch {
      toast.error(dictionary["campaigns-load-error"][language]);
    } finally {
      setLoadingAds(false);
    }
  };

  const handleSelectAdAccount = async (adAccountId: string) => {
    if (adAccountId === selectedAdAccount) return;
    setSelectedAdAccount(adAccountId);
    setMetrics(null);
    setAdsTable([]);
    try {
      await updateMetaAdAccount(adAccountId);
      await loadData(adAccountId, dateStart, dateEnd);
    } catch {
      toast.error(dictionary["campaigns-load-error"][language]);
    }
  };

  const handleSelectBusiness = async (businessId: string) => {
    if (businessId === selectedBusiness) return;
    setSelectedBusiness(businessId);
    setSelectedAdAccount(null);
    setMetrics(null);
    setAdsTable([]);
    setAdAccounts([]);
    try {
      const accounts = await getMetaAdAccounts(businessId);
      const accountList = accounts.adaccounts || [];
      setAdAccounts(accountList);
      if (accountList.length > 0) {
        const firstId = accountList[0].id;
        setSelectedAdAccount(firstId);
        await updateMetaAdAccount(firstId);
        await loadData(firstId, dateStart, dateEnd);
      }
    } catch {
      toast.error(dictionary["campaigns-load-error"][language]);
    }
  };

  const handleDateFilterApply = (startDate: Date, endDate: Date, label: string) => {
    const start = formatDateToYMD(startDate);
    const end = formatDateToYMD(endDate);
    setDateStart(start);
    setDateEnd(end);
    setDateLabel(label);
    if (selectedAdAccount) {
      loadData(selectedAdAccount, start, end);
    }
  };

  const handleSort = (column: string) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortColumn(column);
      setSortDirection("desc");
    }
  };

  const handleConnectMeta = async () => {
    setConnectError(null);
    setConnecting(true);
    try {
      const response = await getMetaOAuthUrl();
      const popup = window.open(
        response.authorizationUrl,
        "meta-oauth",
        "width=600,height=700,scrollbars=yes"
      );

      intervalRef.current = setInterval(async () => {
        if (popup?.closed) {
          if (intervalRef.current) clearInterval(intervalRef.current);
          intervalRef.current = null;
          try {
            const meta = await getMetaIntegration();
            if (meta?.is_active) {
              setConnecting(false);
              toast.success(dictionary["integration-added"][language]);
              await loadIntegration();
              return;
            }
          } catch {
            // Integration not found
          }
          setConnecting(false);
          setConnectError(dictionary["meta-connect-error"][language]);
        }
      }, 500);
    } catch {
      setConnecting(false);
      setConnectError(dictionary["meta-connect-error"][language]);
    }
  };

  const sortedAds = [...adsTable].sort((a, b) => {
    const aVal = (a as any)[sortColumn] ?? 0;
    const bVal = (b as any)[sortColumn] ?? 0;
    if (typeof aVal === "string") {
      return sortDirection === "asc" ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
    }
    return sortDirection === "asc" ? aVal - bVal : bVal - aVal;
  });

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <Loader />
      </div>
    );
  }

  // Empty state: no Meta connected
  if (!integration?.is_active) {
    return (
      <div className="bg-[#F5F6FA] px-4 md:p-8 md:ml-4 min-h-screen">
        <h1 className="text-xl md:text-2xl font-bold mb-6">
          {dictionary["campaigns"][language]}
        </h1>
        <div className="flex flex-col items-center justify-center py-20">
          <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mb-6">
            <BarChart3 className="w-10 h-10 text-gray-400" />
          </div>
          <h2 className="text-lg font-semibold text-gray-800 mb-2">
            {dictionary["campaigns-empty-title"][language]}
          </h2>
          <p className="text-sm text-gray-500 mb-6 text-center max-w-md">
            {dictionary["campaigns-empty-description"][language]}
          </p>
          {connectError && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700 max-w-md w-full text-center">
              {connectError}
            </div>
          )}
          <button
            className="flex items-center gap-2 px-6 py-3 bg-[#0081FB] text-white rounded-lg hover:bg-[#006AD4] transition-colors font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
            onClick={handleConnectMeta}
            disabled={connecting}
          >
            {connecting ? (
              <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
            ) : (
              <>
                {dictionary["campaigns-connect-meta"][language]}
                <ArrowRight className="w-4 h-4" />
              </>
            )}
          </button>
        </div>
      </div>
    );
  }

  const selectedAccountName = adAccounts.find((a) => a.id === selectedAdAccount)?.name || selectedAdAccount;
  const hasActiveCampaigns = (metrics?.running_campaigns?.length ?? 0) > 0;
  const hasAnyMetrics = metrics && ((metrics.reach ?? 0) > 0 || (metrics.impressions ?? 0) > 0 || (metrics.link_clicks ?? 0) > 0);

  return (
    <div className="bg-[#F5F6FA] px-4 md:p-4 md:ml-4 min-h-screen">
      <h1 className="text-xl md:text-2xl font-bold mb-3">
        {dictionary["campaigns"][language]}
      </h1>

      <div className="flex gap-4">
        {/* Left sidebar: Business selector + Ad accounts */}
        <div className="w-64 flex-shrink-0">
          {/* Business dropdown */}
          {businesses.length > 1 && (
            <div className="mb-4">
              <h2 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2 px-1">
                {dictionary["campaigns-portfolio"][language]}
              </h2>
              <select
                className="w-full p-2 bg-white border border-gray-200 rounded-lg text-sm"
                value={selectedBusiness || ""}
                onChange={(e) => handleSelectBusiness(e.target.value)}
              >
                {businesses.map((biz) => (
                  <option key={biz.id} value={biz.id}>{biz.name}</option>
                ))}
              </select>
            </div>
          )}

          {/* Ad accounts list */}
          {adAccounts.length > 0 && (
            <>
              <h2 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3 px-1">
                {dictionary["campaigns-ad-accounts"][language]}
              </h2>
              <div className="space-y-1">
                {adAccounts.map((account) => (
                  <button
                    key={account.id}
                    className={`w-full p-3 rounded-lg text-left transition-all text-sm ${
                      selectedAdAccount === account.id
                        ? "bg-white border border-[#0081FB] shadow-sm"
                        : "bg-white border border-gray-200 hover:border-gray-300"
                    }`}
                    onClick={() => handleSelectAdAccount(account.id)}
                  >
                    <p className={`font-medium truncate ${selectedAdAccount === account.id ? "text-[#0081FB]" : "text-gray-800"}`}>
                      {account.name}
                    </p>
                    <p className="text-xs text-gray-400 truncate">{account.id}</p>
                  </button>
                ))}
              </div>
            </>
          )}
        </div>

        {/* Right: Content area */}
        <div className="flex-1 min-w-0">
          {/* Header: account name + date filter + refresh */}
          <div className="flex items-center justify-between mb-4">
            <div>
              <p className="text-sm text-gray-500">{selectedAccountName}</p>
              {dateLabel && (
                <p className="text-xs text-gray-400 mt-1">{dateLabel}</p>
              )}
            </div>
            <div className="flex items-center gap-3">
              <div className="[&_.absolute]:right-0 [&_.absolute]:left-auto">
                <DateFilter onFilterApply={handleDateFilterApply} />
              </div>
              <button
                className="p-2 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                onClick={() => selectedAdAccount && loadData(selectedAdAccount, dateStart, dateEnd)}
                disabled={loadingMetrics || loadingAds}
              >
                <RefreshCw className={`w-4 h-4 text-gray-600 ${(loadingMetrics || loadingAds) ? "animate-spin" : ""}`} />
              </button>
            </div>
          </div>

          {/* Metrics cards */}
          {loadingMetrics ? (
            <div className="flex justify-center py-10">
              <Loader />
            </div>
          ) : metrics && (hasActiveCampaigns || hasAnyMetrics) ? (
            <>
              {hasActiveCampaigns && (
                <div className="mb-4">
                  <h2 className="text-sm font-semibold text-gray-600 uppercase tracking-wide mb-2">
                    {dictionary["campaigns-active"][language]} ({metrics.running_campaigns.length})
                  </h2>
                  <div className="flex flex-wrap gap-2">
                    {metrics.running_campaigns.map((name, idx) => (
                      <span key={idx} className="px-3 py-1.5 bg-green-50 text-green-700 text-sm rounded-full border border-green-200">
                        {name}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-4">
                <MetricCard icon={<Users className="w-5 h-5 text-blue-600" />} label={dictionary["campaigns-metric-reach"][language]} value={formatNumber(metrics.reach ?? 0)} />
                <MetricCard icon={<Eye className="w-5 h-5 text-purple-600" />} label={dictionary["campaigns-metric-impressions"][language]} value={formatNumber(metrics.impressions ?? 0)} />
                <MetricCard icon={<MousePointerClick className="w-5 h-5 text-orange-600" />} label={dictionary["campaigns-metric-clicks"][language]} value={formatNumber(metrics.link_clicks ?? 0)} />
                <MetricCard icon={<Video className="w-5 h-5 text-teal-600" />} label={dictionary["campaigns-metric-video-3s"][language]} value={formatNumber(metrics.video_3s ?? 0)} />
                <MetricCard icon={<Video className="w-5 h-5 text-indigo-600" />} label={dictionary["campaigns-metric-video-50"][language]} value={formatNumber(metrics.video_50 ?? 0)} />
                <MetricCard icon={<BarChart3 className="w-5 h-5 text-gray-600" />} label={dictionary["campaigns-metric-frequency"][language]} value={(metrics.frequency ?? 0).toFixed(2)} />
              </div>
            </>
          ) : !loadingAds ? (
            <div className="flex flex-col items-center justify-center py-10 mb-6">
              <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
                <Megaphone className="w-8 h-8 text-gray-400" />
              </div>
              <h2 className="text-lg font-semibold text-gray-800 mb-2">
                {dictionary["campaigns-no-active"][language]}
              </h2>
              <p className="text-sm text-gray-500 text-center max-w-md">
                {dictionary["campaigns-no-active-description"][language]}
              </p>
            </div>
          ) : null}

          {/* Ads Table */}
          {loadingAds ? (
            <div className="flex justify-center py-10">
              <Loader />
            </div>
          ) : adsTable.length > 0 ? (
            <div className="bg-white rounded-lg border border-gray-200 overflow-hidden mb-24">
              <div className="overflow-x-auto scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100" style={{ scrollbarWidth: "thin" }}>
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-gray-50 border-b border-gray-200">
                      <SortableHeader column="effective_status" label={dictionary["campaigns-col-status"][language]} currentSort={sortColumn} direction={sortDirection} onSort={handleSort} />
                      <SortableHeader column="ad_name" label={dictionary["campaigns-col-ad-name"][language]} currentSort={sortColumn} direction={sortDirection} onSort={handleSort} />
                      <SortableHeader column="campaign_name" label={dictionary["campaigns-col-campaign"][language]} currentSort={sortColumn} direction={sortDirection} onSort={handleSort} />
                      <SortableHeader column="results" label={dictionary["campaigns-col-results"][language]} currentSort={sortColumn} direction={sortDirection} onSort={handleSort} />
                      <SortableHeader column="cost_per_result" label={dictionary["campaigns-col-cost-result"][language]} currentSort={sortColumn} direction={sortDirection} onSort={handleSort} />
                      <SortableHeader column="budget" label={dictionary["campaigns-col-budget"][language]} currentSort={sortColumn} direction={sortDirection} onSort={handleSort} />
                      <SortableHeader column="spend" label={dictionary["campaigns-col-spend"][language]} currentSort={sortColumn} direction={sortDirection} onSort={handleSort} />
                      <SortableHeader column="impressions" label={dictionary["campaigns-col-impressions"][language]} currentSort={sortColumn} direction={sortDirection} onSort={handleSort} />
                      <SortableHeader column="reach" label={dictionary["campaigns-col-reach"][language]} currentSort={sortColumn} direction={sortDirection} onSort={handleSort} />
                      <SortableHeader column="frequency" label={dictionary["campaigns-col-frequency"][language]} currentSort={sortColumn} direction={sortDirection} onSort={handleSort} />
                      <SortableHeader column="cpm" label={dictionary["campaigns-col-cpm"][language]} currentSort={sortColumn} direction={sortDirection} onSort={handleSort} />
                      <SortableHeader column="cpc" label={dictionary["campaigns-col-cpc"][language]} currentSort={sortColumn} direction={sortDirection} onSort={handleSort} />
                      <SortableHeader column="ctr" label={dictionary["campaigns-col-ctr"][language]} currentSort={sortColumn} direction={sortDirection} onSort={handleSort} />
                      <SortableHeader column="purchases" label={dictionary["campaigns-col-purchases"][language]} currentSort={sortColumn} direction={sortDirection} onSort={handleSort} />
                      <SortableHeader column="roas" label={dictionary["campaigns-col-roas"][language]} currentSort={sortColumn} direction={sortDirection} onSort={handleSort} />
                      <SortableHeader column="hook_rate" label={dictionary["campaigns-col-hook-rate"][language]} currentSort={sortColumn} direction={sortDirection} onSort={handleSort} />
                      <SortableHeader column="thruplay" label={dictionary["campaigns-col-thruplay"][language]} currentSort={sortColumn} direction={sortDirection} onSort={handleSort} />
                    </tr>
                  </thead>
                  <tbody>
                    {sortedAds.map((ad) => (
                      <tr key={ad.ad_id} className="border-b border-gray-100 hover:bg-gray-50">
                        <td className="px-3 py-3">
                          <div className={`w-9 h-5 rounded-full relative ${
                            ad.effective_status === "ACTIVE" ? "bg-green-500" : "bg-gray-300"
                          }`}>
                            <div className={`w-3.5 h-3.5 rounded-full bg-white absolute top-[3px] transition-all ${
                              ad.effective_status === "ACTIVE" ? "right-[3px]" : "left-[3px]"
                            }`} />
                          </div>
                        </td>
                        <td className="px-3 py-3 font-medium text-gray-800 max-w-[200px] truncate">{ad.ad_name}</td>
                        <td className="px-3 py-3 text-gray-600 max-w-[150px] truncate">{ad.campaign_name}</td>
                        <td className="px-3 py-3 text-right">{formatNumber(ad.results)}</td>
                        <td className="px-3 py-3 text-right">{formatCurrency(ad.cost_per_result)}</td>
                        <td className="px-3 py-3 text-right">{ad.budget ? formatCurrency(ad.budget) : "—"}</td>
                        <td className="px-3 py-3 text-right">{formatCurrency(ad.spend)}</td>
                        <td className="px-3 py-3 text-right">{formatNumber(ad.impressions)}</td>
                        <td className="px-3 py-3 text-right">{formatNumber(ad.reach)}</td>
                        <td className="px-3 py-3 text-right">{ad.frequency.toFixed(2)}</td>
                        <td className="px-3 py-3 text-right">{formatCurrency(ad.cpm)}</td>
                        <td className="px-3 py-3 text-right">{formatCurrency(ad.cpc)}</td>
                        <td className="px-3 py-3 text-right">{(ad.ctr).toFixed(2)}%</td>
                        <td className="px-3 py-3 text-right">{formatNumber(ad.purchases)}</td>
                        <td className="px-3 py-3 text-right">{ad.roas.toFixed(2)}</td>
                        <td className="px-3 py-3 text-right">{(ad.hook_rate * 100).toFixed(1)}%</td>
                        <td className="px-3 py-3 text-right">{formatNumber(ad.thruplay)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
};

const SortableHeader = ({
  column, label, currentSort, direction, onSort,
}: {
  column: string; label: string; currentSort: string; direction: "asc" | "desc"; onSort: (col: string) => void;
}) => (
  <th
    className="px-3 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide cursor-pointer hover:text-gray-700 whitespace-nowrap"
    onClick={() => onSort(column)}
  >
    <div className="flex items-center gap-1">
      {label}
      {currentSort === column && (
        direction === "asc" ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />
      )}
    </div>
  </th>
);

const MetricCard = ({ icon, label, value }: { icon: React.ReactNode; label: string; value: string }) => (
  <div className="bg-white p-4 rounded-lg border border-gray-200">
    <div className="flex items-center gap-2 mb-2">
      {icon}
      <span className="text-xs text-gray-500 font-medium">{label}</span>
    </div>
    <p className="text-2xl font-bold text-gray-800">{value}</p>
  </div>
);

const formatNumber = (num: number): string => {
  if (num >= 1_000_000) return (num / 1_000_000).toFixed(1) + "M";
  if (num >= 1_000) return (num / 1_000).toFixed(1) + "K";
  return num.toLocaleString();
};

const formatCurrency = (num: number): string => {
  if (num === 0) return "$0";
  if (num < 0) return "-$" + Math.abs(num).toFixed(2);
  if (num < 0.01) return "<$0.01";
  return "$" + num.toFixed(2);
};

export default CampaignsPage;
